import React, { useState } from "react";

let useNavigate;
try { useNavigate = require("react-router-dom").useNavigate; }
catch { useNavigate = () => (path) => { window.location.href = path; }; }

let ameonLogo = null;
try { ameonLogo = require("../ameon.jpg"); } catch {}
const LOGO_SRC = ameonLogo || "/logo192.png";

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { -webkit-font-smoothing: antialiased; }
    body {
      font-family: 'DM Sans', system-ui, sans-serif;
      color: #1a1a2e;
      background: linear-gradient(135deg, #f0f7ff 0%, #fff5f4 50%, #f5f3ff 100%);
      background-attachment: fixed;
    }
    button { font-family: inherit; cursor: pointer; }
    input  { font-family: inherit; font-size: 15px; }

    :root {
      --blue: #4f8ef7;
      --blue-light: #eff6ff;
      --blue-mid: #bfdbfe;
      --ink: #1a1a2e;
      --ink-muted: #555;
      --ink-faint: #888;
      --border: #f0f0f0;
      --border-strong: #e0e0e0;
      --green: #52c97e;
      --radius-md: 12px;
      --radius-lg: 20px;
      --radius-xl: 28px;
      --text-xs: 12px;
      --text-sm: 13px;
      --text-base: 15px;
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes pulseDot {
      0%, 100% { box-shadow: 0 0 0 0 rgba(82,201,126,0.5); }
      50%       { box-shadow: 0 0 0 8px rgba(82,201,126,0); }
    }
    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.92); }
      to   { opacity: 1; transform: scale(1); }
    }

    .fp-card      { animation: fadeUp 0.65s cubic-bezier(.22,.9,.36,1) both; }
    .success-anim { animation: scaleIn 0.5s cubic-bezier(.22,.9,.36,1) both; }

    .fp-input {
      width: 100%;
      padding: 13px 16px;
      border: 1.5px solid var(--border-strong);
      border-radius: var(--radius-md);
      font-family: 'DM Sans', sans-serif;
      font-size: var(--text-base);
      color: var(--ink);
      background: white;
      transition: border-color 0.2s, box-shadow 0.2s;
      outline: none;
    }
    .fp-input:focus {
      border-color: var(--blue);
      box-shadow: 0 0 0 3px rgba(79,142,247,.12);
    }
    .fp-input::placeholder { color: var(--ink-faint); }

    .btn-fp {
      width: 100%;
      padding: 14px;
      border-radius: 50px;
      border: none;
      background: linear-gradient(135deg, var(--blue), #6366f1);
      color: #fff;
      font-family: 'DM Sans', sans-serif;
      font-size: var(--text-base);
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
      box-shadow: 0 4px 20px rgba(79,142,247,0.35);
      display: flex; align-items: center; justify-content: center; gap: 8px;
    }
    .btn-fp:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(79,142,247,0.45); }
    .btn-fp:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

    .pulse-dot {
      width: 8px; height: 8px; background: var(--green);
      border-radius: 50%; display: inline-block;
      animation: pulseDot 2s infinite;
    }
  `}</style>
);

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail]         = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState("");
  const [sent, setSent]           = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setError("Please enter a valid email address."); return; }

    setIsLoading(true);
    setError("");
    try {
      await fetch("https://api.ameontech.com/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #f0f7ff 0%, #fff5f4 50%, #f5f3ff 100%)",
      padding: "24px 16px",
      position: "relative", overflow: "hidden",
    }}>
      <GlobalStyles />

      {/* Background blobs */}
      <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "#4f8ef7", filter: "blur(120px)", opacity: 0.07, top: -100, right: -100, pointerEvents: "none" }} />
      <div style={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "#a78bfa", filter: "blur(100px)", opacity: 0.07, bottom: -80, left: -80, pointerEvents: "none" }} />

      <div className="fp-card" style={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 2 }}>

        <div style={{
          background: "rgba(255,255,255,0.92)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)",
          padding: "44px 40px",
          boxShadow: "0 8px 40px rgba(0,0,0,0.08)",
          backdropFilter: "blur(12px)",
        }}>

          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 36, cursor: "pointer" }}
            onClick={() => navigate("/")}>
            <img src={LOGO_SRC} alt="InvoiceCloud"
              style={{ width: 34, height: 34, borderRadius: 9, objectFit: "contain", border: "1px solid var(--border)" }}
              onError={e => e.currentTarget.style.display = "none"} />
            <div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 20, color: "var(--ink)", lineHeight: 1 }}>
                Invoice<span style={{ color: "var(--blue)" }}>Cloud</span>
              </div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "1.4px", color: "var(--blue)", textTransform: "uppercase" }}>Canada</div>
            </div>
          </div>

          {/* ── SUCCESS STATE ── */}
          {sent ? (
            <div className="success-anim" style={{ textAlign: "center" }}>
              <div style={{
                width: 72, height: 72,
                background: "linear-gradient(135deg, #52c97e, #34d399)",
                borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px",
                fontSize: 32,
                boxShadow: "0 8px 28px rgba(82,201,126,0.3)",
              }}>✉️</div>

              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 800, color: "var(--ink)", marginBottom: 10, letterSpacing: "-0.4px" }}>
                Check Your Inbox
              </h2>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-base)", color: "var(--ink-muted)", lineHeight: 1.75, marginBottom: 8 }}>
                If <strong style={{ color: "var(--ink)" }}>{email}</strong> is registered with InvoiceCloud Canada, we've sent a password reset link to that address.
              </p>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-faint)", marginBottom: 32 }}>
                Please also check your spam folder. The link is valid for 30 minutes.
              </p>

              {/* Steps */}
              <div style={{ background: "var(--blue-light)", border: "1px solid var(--blue-mid)", borderRadius: "var(--radius-lg)", padding: "20px", marginBottom: 28, textAlign: "left" }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--blue)", marginBottom: 14 }}>Next Steps</p>
                {[
                  { icon: "📧", text: "Open the email from InvoiceCloud Canada" },
                  { icon: "🔗", text: "Click the 'Reset Password' link" },
                  { icon: "🔒", text: "Set your new password and sign in" },
                ].map((step, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: i < 2 ? 12 : 0 }}>
                    <span style={{ fontSize: 16 }}>{step.icon}</span>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "var(--ink-muted)" }}>{step.text}</span>
                  </div>
                ))}
              </div>

              <button className="btn-fp" onClick={() => navigate("/login")}>
                Back to Login →
              </button>

              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                style={{
                  background: "none", border: "none",
                  color: "var(--blue)", fontFamily: "'DM Sans', sans-serif",
                  fontSize: "var(--text-sm)", fontWeight: 600,
                  cursor: "pointer", marginTop: 16, display: "block", width: "100%",
                }}
              >
                Try a different email address
              </button>
            </div>

          ) : (
            /* ── FORM STATE ── */
            <>
              <div style={{
                width: 60, height: 60,
                background: "var(--blue-light)",
                border: "1px solid var(--blue-mid)",
                borderRadius: 16,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 28, marginBottom: 20,
              }}>🔑</div>

              <h1 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(24px,3vw,30px)",
                fontWeight: 800, color: "var(--ink)",
                letterSpacing: "-0.4px", marginBottom: 8, lineHeight: 1.15,
              }}>
                Forgot Your Password?
              </h1>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "var(--text-base)", color: "var(--ink-muted)",
                lineHeight: 1.7, marginBottom: 28,
              }}>
                No worries — enter your email address and we'll send you a secure reset link right away.
              </p>

              {error && (
                <div style={{
                  background: "#fef2f2", border: "1px solid #fecaca",
                  borderRadius: "var(--radius-md)", padding: "11px 15px",
                  marginBottom: 18, display: "flex", alignItems: "center", gap: 9,
                  fontFamily: "'DM Sans', sans-serif", fontSize: "var(--text-sm)", color: "#991b1b",
                }}>
                  <span>❌</span> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={{
                    display: "block",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "var(--text-sm)", fontWeight: 600,
                    color: "var(--ink)", marginBottom: 7,
                  }}>
                    Email Address
                  </label>
                  <input
                    className="fp-input"
                    type="email"
                    placeholder="you@company.ca"
                    value={email}
                    onChange={e => { setEmail(e.target.value); if (error) setError(""); }}
                    autoComplete="email"
                    autoFocus
                    disabled={isLoading}
                  />
                </div>

                <button type="submit" className="btn-fp" disabled={isLoading} style={{ marginTop: 4 }}>
                  {isLoading
                    ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }} /> Sending...</>
                    : "Send Reset Link →"
                  }
                </button>
              </form>

              {/* Info note */}
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                background: "#fafafa", border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)", padding: "12px 14px", marginTop: 18,
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "var(--ink-faint)", lineHeight: 1.65 }}>
                  The reset link will expire after 30 minutes. If you don't receive an email, please check your spam folder or contact us at{" "}
                  <a href="mailto:billing@invoicecloud.ca" style={{ color: "var(--blue)", fontWeight: 600 }}>billing@invoicecloud.ca</a>.
                </p>
              </div>
            </>
          )}

          {/* Back to login */}
          {!sent && (
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <button
                type="button"
                onClick={() => navigate("/login")}
                style={{
                  background: "none", border: "none",
                  color: "var(--ink-faint)", fontFamily: "'DM Sans', sans-serif",
                  fontSize: "var(--text-sm)", cursor: "pointer", padding: 0,
                  display: "inline-flex", alignItems: "center", gap: 5,
                  transition: "color 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.color = "var(--blue)"}
                onMouseLeave={e => e.currentTarget.style.color = "var(--ink-faint)"}
              >
                ← Back to Login
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p style={{ textAlign: "center", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "var(--ink-faint)", marginTop: 20 }}>
          © 2026 InvoiceCloud Canada Inc. · Made in Canada 🍁
        </p>
      </div>
    </div>
  );
}